import { useEffect, useState } from "react";
import { usePageState } from "@/contexts/NavigationContext";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import FileUploader from "@/components/FileUploader";
import ExcelBulkActions from "@/components/ExcelBulkActions";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import {
  UsersIcon, ReceiptIcon, CalendarIcon, MedalIcon, ChartLineUpIcon,
  PlusIcon, EyeIcon, CheckCircleIcon, XCircleIcon, ArrowsClockwiseIcon,
  PencilSimpleIcon, XIcon, PrinterIcon, IdentificationCardIcon, PaperclipIcon,
  CalculatorIcon, DownloadIcon, MoneyIcon, WarningIcon, ClockCountdownIcon,
  UserCircleIcon, FilePdfIcon, EnvelopeSimpleIcon, ListChecksIcon, BriefcaseIcon,
  CheckIcon
} from "@phosphor-icons/react";
import CargosTab from "@/pages/_hr/CargosTab";
import MiPerfilTab, { uploadRespaldo } from "@/pages/_hr/MiPerfilTab";
import HrReportesTab, { PayrollParamsEditor } from "@/pages/_hr/HrReportesTab";
import { ExtrasModal, PayModal, DeptReport } from "@/pages/_hr/PayrollTools";
import { EmployeeLinkFields, EstadoModal } from "@/pages/_hr/EmployeeExtras";
import LoansPanel from "@/pages/_hr/LoansPanel";
import { openPdf } from "@/pages/_hr/download";
import { useLiveSearch, SearchBox } from "@/components/LiveSearch";

export default function HumanResources() {
  const [tab, setTab] = useState("empleados");
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/hr/me").then(({ data }) => { setMe(data); if (!data.is_hr_admin) setTab("perfil"); })
      .catch(() => { setMe({ is_hr_admin: false }); setTab("perfil"); });
  }, []);
  if (!me) return <Layout title="Recursos Humanos"><div className="p-10 text-center text-text-muted">Cargando…</div></Layout>;
  if (!me.is_hr_admin) {
    return (
      <Layout title="Recursos Humanos" subtitle="Mi perfil: expediente, saldos, colillas y solicitudes">
        <div className="flex gap-1 mb-5 border-b border-border/60" data-testid="hr-tabs">
          <T active icon={UserCircleIcon} testid="tab-perfil" onClick={() => {}}>Mi perfil</T>
        </div>
        <MiPerfilTab />
      </Layout>
    );
  }
  return (
    <Layout title="Recursos Humanos" subtitle="Expediente, planilla Honduras (IHSS/RAP/INFOP/ISR), ausencias y actas">
      <div className="flex gap-1 mb-5 border-b border-border/60 overflow-x-auto" data-testid="hr-tabs">
        <T active={tab === "perfil"} onClick={() => setTab("perfil")} icon={UserCircleIcon} testid="tab-perfil">Mi perfil</T>
        <T active={tab === "empleados"} onClick={() => setTab("empleados")} icon={UsersIcon} testid="tab-empleados">Empleados</T>
        <T active={tab === "cargos"} onClick={() => setTab("cargos")} icon={BriefcaseIcon} testid="tab-cargos">Cargos</T>
        <T active={tab === "vencimientos"} onClick={() => setTab("vencimientos")} icon={ClockCountdownIcon} testid="tab-vencimientos">Vencimientos</T>
        <T active={tab === "nomina"} onClick={() => setTab("nomina")} icon={ReceiptIcon} testid="tab-nomina">Nómina</T>
        <T active={tab === "ausencias"} onClick={() => setTab("ausencias")} icon={CalendarIcon} testid="tab-ausencias">Permisos / Vacaciones</T>
        <T active={tab === "actas"} onClick={() => setTab("actas")} icon={MedalIcon} testid="tab-actas">Actas</T>
        <T active={tab === "reportes"} onClick={() => setTab("reportes")} icon={ChartLineUpIcon} testid="tab-reportes">Reportes</T>
      </div>
      {tab === "empleados" && <EmpleadosTab />}
      {tab === "cargos" && <CargosTab />}
      {tab === "vencimientos" && <VencimientosTab />}
      {tab === "nomina" && <NominaTab />}
      {tab === "ausencias" && <AusenciasTab />}
      {tab === "actas" && <ActasTab />}
      {tab === "reportes" && <ReportesTab isAdmin={!!me.is_admin} />}
      {tab === "perfil" && <MiPerfilTab />}
    </Layout>
  );
}

function T({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px transition whitespace-nowrap ${active ? "border-agro-green text-agro-green" : "border-transparent text-text-muted hover:text-text-primary"}`}>
      <Icon size={14} /> {children}
    </button>
  );
}

/* ===================== 13.1 EMPLEADOS ===================== */
function EmpleadosTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [estadoEmp, setEstadoEmp] = useState(null);
  const [q, setQ] = usePageState("q", "");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/hr/employees"); setItems(data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const busqueda = useLiveSearch(items, (e) => [e.codigo, e.nombre, e.nombre_completo, e.identidad, e.cargo, e.cargo_nombre, e.departamento, e.email],
    { estado: [q, setQ] });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <SearchBox busqueda={busqueda} placeholder="Buscar empleado por nombre, código, identidad o cargo…" testid="emp-search" label="Buscar empleados" className="max-w-xl" />
        <div className="flex flex-wrap items-center gap-2">
          <ExcelBulkActions
            templateUrl="/hr/employees-template"
            exportUrl="/hr/employees-export"
            importUrl="/hr/employees-import"
            templateFilename="plantilla_empleados_agroport.xlsx"
            exportFilename="empleados_export.xlsx"
            resultLabels={{ created: "Importados", updated: "Saltados" }}
            onImported={load}
            testidPrefix="employees"
          />
          <button onClick={() => setEditing({})} data-testid="new-employee" className="bg-agro-green text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 hover:bg-agro-green-hover active:scale-95">
            <PlusIcon size={14} weight="bold" /> Nuevo empleado
          </button>
        </div>
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">Código</th><th className="p-3">Nombre</th>
                <th className="p-3">Puesto / Dpto.</th><th className="p-3">Ingreso</th>
                <th className="p-3 text-right">Salario</th><th className="p-3">Estado</th>
                <th className="p-3 pr-5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="7" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              : busqueda.resultados.length === 0 ? <tr><td colSpan="7" className="p-10 text-center text-text-muted">{busqueda.activa ? `Sin empleados que coincidan con «${busqueda.texto}».` : "Sin empleados. Crea el primero."}</td></tr>
              : busqueda.resultados.map((e) => (
                <tr key={e.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`emp-row-${e.codigo}`}>
                  <td className="p-3 pl-5 font-mono text-xs text-agro-green">{e.codigo}</td>
                  <td className="p-3 text-sm">{e.nombre}<div className="text-[10px] text-text-muted">{e.identidad}</div></td>
                  <td className="p-3 text-xs">{e.puesto}<div className="text-[10px] text-text-muted">{e.departamento}</div></td>
                  <td className="p-3 text-xs text-text-secondary">{fmtDate(e.fecha_ingreso)}</td>
                  <td className="p-3 text-right text-sm font-medium">{e.salario_base == null ? "—" : fmtL(e.salario_base)}</td>
                  <td className="p-3"><EstadoBadge e={e.estado} /></td>
                  <td className="p-3 pr-5 text-right">
                    <button onClick={() => setViewing(e.id)} className="p-1.5 text-text-muted hover:text-agro-green" title="Ver expediente" aria-label="Ver expediente"><EyeIcon size={14} /></button>
                    <button onClick={() => setEditing(e)} className="p-1.5 text-text-muted hover:text-agro-green" title="Editar" aria-label="Editar"><PencilSimpleIcon size={14} /></button>
                    <button onClick={() => setEstadoEmp(e)} className="p-1.5 text-text-muted hover:text-agro-yellow" title="Cambiar estado" data-testid={`estado-${e.codigo}`}><ListChecksIcon size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <EmployeeEditor employee={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {viewing && <EmployeeViewer eid={viewing} onClose={() => setViewing(null)} />}
      {estadoEmp && <EstadoModal employee={estadoEmp} onClose={() => setEstadoEmp(null)} onSaved={() => { setEstadoEmp(null); load(); }} />}
    </div>
  );
}

/* ============== RH-07: Alertas de vencimiento de contratos ============== */
function VencimientosTab() {
  const [data, setData] = useState({ resumen: {}, items: [] });
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [includeExpired, setIncludeExpired] = useState(true);
  const [notifying, setNotifying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/contract-expirations", {
        params: { days_ahead: days, include_expired: includeExpired },
      });
      setData(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, includeExpired]);

  const notify = async () => {
    if (!data.items?.length) {
      toast.info("No hay contratos por notificar en esta ventana");
      return;
    }
    if (!window.confirm(`Enviar recordatorio por email a admins/RRHH con ${data.items.length} contrato(s)?`)) return;
    setNotifying(true);
    try {
      const { data: r } = await api.post("/hr/contract-expirations/notify", {
        days_ahead: days, include_expired: includeExpired,
      });
      if (r.sent) toast.success(`Email enviado a ${r.to.length} destinatario(s)`);
      else toast.info("No hay contratos que notificar");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setNotifying(false); }
  };

  const r = data.resumen || {};
  const badge = (estado, dias) => {
    if (estado === "vencido") return <span className="text-[10px] uppercase px-2 py-0.5 rounded border bg-red-500/15 text-red-400 border-red-500/40 font-semibold">Vencido ({Math.abs(dias)}d)</span>;
    if (estado === "critico") return <span className="text-[10px] uppercase px-2 py-0.5 rounded border bg-agro-yellow/15 text-agro-yellow border-agro-yellow/40 font-semibold">Crítico ({dias}d)</span>;
    return <span className="text-[10px] uppercase px-2 py-0.5 rounded border bg-agro-green/10 text-agro-green border-agro-green/30 font-semibold">Próximo ({dias}d)</span>;
  };

  return (
    <div className="space-y-4" data-testid="vencimientos-tab">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiVenc label="Vencidos" v={r.vencidos || 0} color="text-red-400" testid="kpi-venc-vencidos" />
        <KpiVenc label="Críticos (≤7d)" v={r.criticos || 0} color="text-agro-yellow" testid="kpi-venc-criticos" />
        <KpiVenc label="Próximos" v={r.proximos || 0} color="text-agro-green" testid="kpi-venc-proximos" />
        <KpiVenc label="Total en alerta" v={r.total || 0} color="text-text-primary" testid="kpi-venc-total" />
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-3 bg-card border border-border/60 rounded-md p-3">
        <label className="text-xs text-text-secondary flex items-center gap-2">
          Ventana:
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="bg-app border border-border rounded px-2 py-1 text-xs focus:border-agro-green"
            data-testid="venc-days-select"
          >
            <option value={7}>7 días</option>
            <option value={15}>15 días</option>
            <option value={30}>30 días</option>
            <option value={60}>60 días</option>
            <option value={90}>90 días</option>
          </select>
        </label>
        <label className="text-xs text-text-secondary flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeExpired}
            onChange={(e) => setIncludeExpired(e.target.checked)}
            className="accent-agro-green"
            data-testid="venc-include-expired"
          />
          Incluir vencidos
        </label>
        <button
          onClick={load}
          className="ml-auto text-xs text-text-muted hover:text-agro-green flex items-center gap-1"
          data-testid="venc-refresh"
        >
          <ArrowsClockwiseIcon size={13} /> Actualizar
        </button>
        <button
          onClick={notify}
          disabled={notifying || (data.items?.length ?? 0) === 0}
          data-testid="venc-notify"
          className="inline-flex items-center gap-1.5 bg-agro-yellow/10 hover:bg-agro-yellow/20 disabled:opacity-40 text-agro-yellow font-semibold text-xs rounded-md px-3 py-1.5 transition"
          title="Enviar recordatorio por email a admins/RRHH"
        >
          {notifying ? "Enviando…" : <><EnvelopeSimpleIcon size={14} className="inline align-[-2px]" aria-hidden="true" /> Enviar recordatorios</>}
        </button>
      </div>

      {/* Tabla */}
      <div className="bg-card border border-border/60 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
            <tr>
              <th className="p-3 text-left pl-4">Código</th>
              <th className="p-3 text-left">Empleado</th>
              <th className="p-3 text-left">Puesto</th>
              <th className="p-3 text-left">Departamento</th>
              <th className="p-3 text-left">Tipo contrato</th>
              <th className="p-3 text-left">Vence</th>
              <th className="p-3 text-left pr-4">Estado</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-8 text-center text-text-muted text-sm">Cargando…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-text-muted text-sm">Sin contratos por vencer en esta ventana.</td></tr>
            ) : (
              data.items.map((e, i) => (
                <tr key={e.empleado_id} className="border-t border-border/40 hover:bg-sidebar/40" data-testid={`venc-row-${i}`}>
                  <td className="p-3 pl-4 text-xs font-mono text-agro-green">{e.codigo}</td>
                  <td className="p-3 text-sm">{e.nombre}</td>
                  <td className="p-3 text-xs text-text-secondary">{e.puesto || "—"}</td>
                  <td className="p-3 text-xs text-text-secondary">{e.departamento || "—"}</td>
                  <td className="p-3 text-xs text-text-muted capitalize">{(e.tipo_contrato || "").replace("_", " ")}</td>
                  <td className="p-3 text-xs font-mono text-text-secondary">{e.fecha_fin_contrato}</td>
                  <td className="p-3 pr-4">{badge(e.estado, e.dias_restantes)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiVenc({ label, v, color, testid }) {
  return (
    <div className="bg-card border border-border/60 rounded-md p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">{label}</div>
      <div className={`text-2xl font-heading font-bold ${color}`}>{v}</div>
    </div>
  );
}


function EstadoBadge({ e }) {
  const m = {
    activo: "bg-agro-green/10 text-agro-green border-agro-green/30",
    inactivo: "bg-text-muted/20 text-text-muted border-border",
    suspendido: "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30",
    baja: "bg-red-500/10 text-red-400 border-red-500/30",
  }[e] || "border-border";
  return <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${m}`}>{e}</span>;
}

function EmployeeEditor({ employee, onClose, onSaved }) {
  const [f, setF] = useState({
    codigo: "", nombre: "", identidad: "", rtn: "", fecha_nacimiento: "", direccion: "", telefono: "", email: "",
    contacto_emergencia_nombre: "", contacto_emergencia_telefono: "", contacto_emergencia_parentesco: "",
    contacto_emergencia_2_nombre: "", contacto_emergencia_2_telefono: "", contacto_emergencia_2_parentesco: "",
    email_institucional: "", telefono_institucional: "",
    cargo_id: "",
    puesto: "", departamento: "", fecha_ingreso: new Date().toISOString().slice(0, 10),
    tipo_contrato: "indefinido", fecha_fin_contrato: "", jornada: "completa", salario_base: 0,
    cuenta_bancaria: "", banco: "", region: "", regiones: [], es_rtc: false, notas: "", user_id: null, rtc_id: null, jefe_id: null,
    afiliaciones: {}, ...employee,
  });
  const [saving, setSaving] = useState(false);
  const [cargos, setCargos] = useState([]);
  // Empleado nuevo: el código se genera en el orden del sistema (EMP-0006…) y se asigna al guardar
  const nuevo = !employee?.id;
  const [codigoSugerido, setCodigoSugerido] = useState("");
  const [codigoManual, setCodigoManual] = useState(false);
  useEffect(() => {
    if (!nuevo) return;
    api.get("/hr/employees-codigo-sugerido").then(({ data }) => setCodigoSugerido(data.codigo || "")).catch(() => setCodigoManual(true));
  }, [nuevo]);
  const codigoOk = nuevo && !codigoManual ? true : Boolean(f.codigo);
  useEffect(() => {
    // Catálogo único (sección Cargos): se recarga al abrir el formulario y cuando cambia el catálogo
    const load = () => api.get("/hr/cargos").then(({ data }) => setCargos(data || [])).catch(() => {});
    load();
    window.addEventListener("hr-cargos-actualizados", load);
    return () => window.removeEventListener("hr-cargos-actualizados", load);
  }, []);
  // Nuevas asignaciones: solo cargos activos. El cargo inactivo que el empleado ya tiene se conserva.
  const cargoOpts = cargos.filter((c) => c.activo !== false || c.id === employee?.cargo_id)
    .map((c) => [c.id, c.activo === false ? `${c.nombre} (inactivo)` : c.nombre]);

  const save = async () => {
    if (!codigoOk || !f.nombre || !f.identidad) return toast.error("Código, nombre e identidad son obligatorios");
    setSaving(true);
    try {
      if (f.id) { await api.put(`/hr/employees/${f.id}`, f); toast.success("Empleado guardado"); }
      else {
        const { data } = await api.post("/hr/employees", { ...f, codigo: codigoManual ? f.codigo : "" });
        toast.success(`Empleado ${data.codigo} guardado`);
      }
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  const m = useModalForm({ value: f, onClose, onSave: save });
  const completo = Boolean(codigoOk && f.nombre && f.identidad);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="employee-editor">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex justify-between items-center z-10">
          <h3 className="font-heading text-lg">{f.id ? `Editar: ${f.nombre}` : "Nuevo empleado"}</h3>
          <div className="flex gap-2 items-center">
            <button onClick={m.cerrar} className="px-3 py-1.5 text-sm border border-border rounded text-text-secondary" data-testid="modal-cancelar">Cancelar</button>
            <button onClick={m.guardar} disabled={saving || m.saving || !completo} data-testid="modal-guardar"
              title={completo ? "" : "Código, nombre e identidad son obligatorios"}
              className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded disabled:opacity-40 disabled:cursor-not-allowed">{saving || m.saving ? "Guardando…" : "Guardar"}</button>
            <BotonCerrar onClick={m.cerrar} />
          </div>
        </div>
        <div className="p-5 space-y-5">
          <Section title="Datos personales">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {nuevo && !codigoManual ? (
                <div data-testid="employee-codigo-auto">
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Código <span className="text-red-400">*</span></label>
                  <div className="w-full bg-app border border-agro-green/40 rounded-md px-3 py-2 text-sm font-mono text-agro-green" data-testid="employee-codigo-sugerido">{codigoSugerido || "…"}</div>
                  <p className="text-[10px] text-text-muted mt-1">Automático, siguiente del sistema (se confirma al guardar) ·{" "}
                    <button type="button" onClick={() => { setCodigoManual(true); setF({ ...f, codigo: codigoSugerido }); }} className="underline hover:text-text-primary" data-testid="employee-codigo-manual">escribir manualmente</button></p>
                </div>
              ) : <Inp l="Código" v={f.codigo} on={(v) => setF({...f, codigo: v})} req />}
              <Inp l="Nombre completo" v={f.nombre} on={(v) => setF({...f, nombre: v})} req span="md:col-span-2" />
              <Inp l="Identidad" v={f.identidad} on={(v) => setF({...f, identidad: v})} req />
              <Inp l="RTN" v={f.rtn} on={(v) => setF({...f, rtn: v})} />
              <Inp l="Fecha nacimiento" type="date" v={f.fecha_nacimiento} on={(v) => setF({...f, fecha_nacimiento: v})} />
              <Inp l="Teléfono" v={f.telefono} on={(v) => setF({...f, telefono: v})} />
              <Inp l="Correo" type="email" v={f.email} on={(v) => setF({...f, email: v})} span="md:col-span-2" />
              <Inp l="Dirección" v={f.direccion} on={(v) => setF({...f, direccion: v})} span="md:col-span-3" />
            </div>
          </Section>
          <Section title="Contacto de emergencia">
            <div className="grid grid-cols-3 gap-3">
              <Inp l="Nombre" v={f.contacto_emergencia_nombre} on={(v) => setF({...f, contacto_emergencia_nombre: v})} />
              <Inp l="Teléfono" v={f.contacto_emergencia_telefono} on={(v) => setF({...f, contacto_emergencia_telefono: v})} />
              <Inp l="Parentesco" v={f.contacto_emergencia_parentesco} on={(v) => setF({...f, contacto_emergencia_parentesco: v})} />
            </div>
          </Section>
          <Section title="Contacto de emergencia #2 (RH-03)">
            <div className="grid grid-cols-3 gap-3">
              <Inp l="Nombre" v={f.contacto_emergencia_2_nombre} on={(v) => setF({...f, contacto_emergencia_2_nombre: v})} />
              <Inp l="Teléfono" v={f.contacto_emergencia_2_telefono} on={(v) => setF({...f, contacto_emergencia_2_telefono: v})} />
              <Inp l="Parentesco" v={f.contacto_emergencia_2_parentesco} on={(v) => setF({...f, contacto_emergencia_2_parentesco: v})} />
            </div>
          </Section>
          <Section title="Comunicaciones institucionales (RH-06)">
            <div className="grid grid-cols-2 gap-3">
              <Inp l="Correo institucional" type="email" v={f.email_institucional} on={(v) => setF({...f, email_institucional: v})} />
              <Inp l="Teléfono institucional" v={f.telefono_institucional} on={(v) => setF({...f, telefono_institucional: v})} />
            </div>
            <div className="text-[10px] text-text-muted mt-2">Si está registrado, las notificaciones automáticas (reformas de perfil, actas, recordatorios) se enviarán a este correo en lugar del personal.</div>
          </Section>
          <Section title="Datos laborales">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <Sel l="Cargo" v={f.cargo_id || ""} on={(v) => setF({...f, cargo_id: v || null})} testid="hr-emp-cargo"
                  opts={[["", "— Sin cargo asignado —"], ...cargoOpts]} />
                {cargos.find((c) => c.id === f.cargo_id)?.activo === false && (
                  <div className="text-[10px] text-agro-yellow mt-1" data-testid="hr-cargo-inactivo-hint">
                    Cargo inactivo: el empleado lo conserva; no se puede asignar a otros empleados.
                  </div>)}
                {cargos.find((c) => c.id === f.cargo_id)?.es_rtc && (
                  <div className="text-[10px] text-agro-green mt-1" data-testid="hr-cargo-rtc-hint">
                    Mientras esté activo con este puesto estará disponible como RTC en clientes, facturación, cobros,
                    GRATIVA (Gratificación Variable) y viáticos.
                  </div>)}
              </div>
              <Inp l="Puesto (libre)" v={f.puesto} on={(v) => setF({...f, puesto: v})} />
              <Inp l="Departamento" v={f.departamento} on={(v) => setF({...f, departamento: v})} />
              <Inp l="Fecha ingreso" type="date" v={f.fecha_ingreso} on={(v) => setF({...f, fecha_ingreso: v})} />
              <Sel l="Tipo de contrato" v={f.tipo_contrato} on={(v) => setF({...f, tipo_contrato: v})} opts={[
                ["indefinido", "Indefinido"], ["termino_fijo", "Término fijo"], ["obra", "Por obra"], ["honorarios", "Honorarios"],
              ]} />
              <Inp
                l={`Fin de contrato${f.tipo_contrato === "indefinido" ? " (no aplica)" : ""}`}
                type="date"
                v={f.fecha_fin_contrato || ""}
                on={(v) => setF({...f, fecha_fin_contrato: v})}
                disabled={f.tipo_contrato === "indefinido"}
              />
              <Sel l="Jornada" v={f.jornada} on={(v) => setF({...f, jornada: v})} opts={[["completa", "Completa"], ["media", "Media"], ["por_hora", "Por hora"]]} />
              <Inp l="Salario base mensual (L.)" type="number" step="0.01" v={f.salario_base} on={(v) => setF({...f, salario_base: parseFloat(v) || 0})} req />
              <Inp l="Banco" v={f.banco} on={(v) => setF({...f, banco: v})} />
              <Inp l="Cuenta bancaria" v={f.cuenta_bancaria} on={(v) => setF({...f, cuenta_bancaria: v})} />
              {!cargos.find((c) => c.id === f.cargo_id)?.es_rtc && <RegionField v={f.region} on={(v) => setF({...f, region: v})} />}
            </div>
            {cargos.find((c) => c.id === f.cargo_id)?.es_rtc && (
              <RegionesRtc v={f.regiones || []} texto={f.region} on={(v) => setF((x) => ({ ...x, regiones: v }))} conUsuario={Boolean(f.user_id)} />
            )}
          </Section>
          <Section title="Vínculos: usuario del sistema, RTC y jefe inmediato">
            <EmployeeLinkFields f={f} setF={setF} />
          </Section>
          <Section title="Afiliaciones legales">
            <div className="grid grid-cols-3 gap-3">
              <Inp l="Nº IHSS" v={f.afiliaciones?.ihss || ""} on={(v) => setF({...f, afiliaciones: {...f.afiliaciones, ihss: v}})} />
              <Inp l="Nº RAP" v={f.afiliaciones?.rap || ""} on={(v) => setF({...f, afiliaciones: {...f.afiliaciones, rap: v}})} />
              <Inp l="Nº INFOP" v={f.afiliaciones?.infop || ""} on={(v) => setF({...f, afiliaciones: {...f.afiliaciones, infop: v}})} />
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

function EmployeeViewer({ eid, onClose }) {
  const [emp, setEmp] = useState(null);
  const [acts, setActs] = useState([]);
  const [docs, setDocs] = useState([]);
  const [docTipo, setDocTipo] = useState("contrato");
  const [docDesc, setDocDesc] = useState("");

  const loadDocs = async () => {
    try {
      const { data } = await api.get(`/hr/employees/${eid}/documents`);
      setDocs(data || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    api.get(`/hr/employees/${eid}`).then(({ data }) => setEmp(data));
    api.get(`/hr/employees/${eid}/acts`).then(({ data }) => setActs(data));
    loadDocs();
    // eslint-disable-next-line
  }, [eid]);

  const attachUploaded = async (file) => {
    // file viene de /api/uploads → { url, key, original_filename, content_type, size, id }
    try {
      const { data } = await api.post(`/hr/employees/${eid}/documents`, {
        tipo: docTipo,
        descripcion: docDesc || "",
        url: file.url,
        key: file.key,
        original_filename: file.original_filename,
        content_type: file.content_type,
        size: file.size,
      });
      setDocs((d) => [data, ...d]);
      setDocDesc("");
      toast.success("Documento agregado al expediente");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const vm = useModalForm({ onClose });
  const removeDoc = async (doc) => {
    if (!window.confirm(`Eliminar "${doc.original_filename || doc.tipo}" del expediente?`)) return;
    try {
      await api.delete(`/hr/employees/${eid}/documents/${doc.id}`);
      setDocs((d) => d.filter((x) => x.id !== doc.id));
      toast.success("Documento eliminado");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  if (!emp) return <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center" {...vm.overlayProps}><div className="text-text-muted">Cargando…</div></div>;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="employee-viewer">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex justify-between items-center z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Expediente oficial</div>
            <h3 className="font-heading text-xl">{emp.nombre}</h3>
            <div className="text-xs text-text-secondary">{emp.codigo} · {emp.puesto} · {emp.departamento}</div>
          </div>
          <button aria-label="Cerrar" onClick={onClose} className="p-1 text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Saldo vacaciones */}
          <div className="bg-agro-green/5 border border-agro-green/30 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-wider text-agro-green mb-1">Saldo de vacaciones</div>
            <div className="grid grid-cols-4 gap-3 text-sm">
              <div><span className="text-text-muted text-xs">Antigüedad:</span><div className="font-medium">{emp.saldo_vacaciones?.anios_servicio} años</div></div>
              <div><span className="text-text-muted text-xs">Anuales:</span><div className="font-medium">{emp.saldo_vacaciones?.dias_anuales}d</div></div>
              <div><span className="text-text-muted text-xs">Tomados:</span><div className="font-medium">{emp.saldo_vacaciones?.dias_tomados}d</div></div>
              <div><span className="text-text-muted text-xs">Disponibles:</span><div className="font-heading text-lg text-agro-green">{emp.saldo_vacaciones?.dias_disponibles}d</div></div>
            </div>
          </div>

          <Info l="Identidad" v={emp.identidad} />
          <Info l="Salario base" v={fmtL(emp.salario_base)} />
          <Info l="Ingreso" v={fmtDate(emp.fecha_ingreso)} />
          <Info l="Contrato" v={emp.tipo_contrato} />
          <Info l="Banco / Cuenta" v={`${emp.banco || "—"} · ${emp.cuenta_bancaria || "—"}`} />
          <Info l="Afiliaciones" v={`IHSS: ${emp.afiliaciones?.ihss || "—"} · RAP: ${emp.afiliaciones?.rap || "—"} · INFOP: ${emp.afiliaciones?.infop || "—"}`} />

          {/* RH-04 — Documentos escaneados (contratos, adendums, etc.) */}
          <div className="border border-border/60 rounded-md p-3" data-testid="employee-documents">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-wider text-text-muted flex items-center gap-1.5">
                <PaperclipIcon size={12} /> Documentos y contratos ({docs.length})
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
              <div className="sm:col-span-1">
                <label className="text-[9px] uppercase tracking-wider text-text-muted mb-1 block">Tipo</label>
                <select
                  value={docTipo}
                  onChange={(e) => setDocTipo(e.target.value)}
                  className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm"
                  data-testid="doc-tipo-select"
                >
                  <option value="contrato">Contrato</option>
                  <option value="adendum">Adendum / Actualización</option>
                  <option value="identidad">Identidad</option>
                  <option value="hoja_vida">Hoja de vida</option>
                  <option value="ihss">IHSS</option>
                  <option value="rap">RAP</option>
                  <option value="infop">INFOP</option>
                  <option value="certificacion">Certificación</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-[9px] uppercase tracking-wider text-text-muted mb-1 block">Descripción (opcional)</label>
                <input
                  value={docDesc}
                  onChange={(e) => setDocDesc(e.target.value)}
                  placeholder="Ej: Contrato indefinido firmado 2026"
                  className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm"
                  data-testid="doc-desc-input"
                />
              </div>
            </div>

            <FileUploader
              folder={`hr/${eid}`}
              kind="document"
              files={[]}
              onUploaded={attachUploaded}
              multiple={false}
              label="Adjuntar contrato / documento"
            />

            {docs.length > 0 && (
              <ul className="mt-3 space-y-1.5" data-testid="employee-documents-list">
                {docs.map((d) => (
                  <li key={d.id} className="flex items-center gap-2 px-3 py-2 bg-app/60 border border-border/60 rounded-md text-sm" data-testid={`doc-row-${d.id}`}>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-agro-green/10 text-agro-green border border-agro-green/30 whitespace-nowrap">
                      {d.tipo}
                    </span>
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 truncate hover:text-agro-green hover:underline"
                      title={d.original_filename || d.descripcion || d.tipo}
                    >
                      {d.original_filename || d.descripcion || `Documento ${d.tipo}`}
                    </a>
                    <span className="text-[10px] text-text-muted whitespace-nowrap">{fmtDate(d.uploaded_at)}</span>
                    <button
                      type="button"
                      onClick={() => removeDoc(d)}
                      className="p-1 text-text-muted hover:text-red-400 hover:bg-red-500/10 rounded transition"
                      title="Eliminar"
                      data-testid={`doc-remove-${d.id}`}
                    >
                      <XIcon size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Actas */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Actas en el expediente ({acts.length})</div>
            {acts.length === 0 ? <div className="text-xs text-text-muted">Sin actas</div>
            : <div className="space-y-2">
                {acts.map((a) => (
                  <div key={a.id} className={`p-2 border rounded text-xs ${a.tipo === "reconocimiento" ? "border-agro-green/30 bg-agro-green/5" : "border-red-500/30 bg-red-500/5"}`}>
                    <div className="flex justify-between">
                      <strong className={a.tipo === "reconocimiento" ? "text-agro-green" : "text-red-400"}>{a.tipo.replace(/_/g, " ").toUpperCase()}</strong>
                      <span>{fmtDate(a.fecha)}</span>
                    </div>
                    <div className="text-text-secondary">{a.motivo}</div>
                    <div className="text-[10px] text-text-muted mt-0.5">Por: {a.emitido_por}</div>
                  </div>
                ))}
              </div>}
          </div>
        </div>
      </div>
    </div>
  );
}

const Info = ({ l, v }) => <div className="flex justify-between border-b border-border/30 py-1 text-sm"><span className="text-text-muted">{l}</span><span>{v}</span></div>;
const Section = ({ title, children }) => <div><div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">{title}</div>{children}</div>;
// RTC: regiones que atiende. Rigen lo que su usuario del sistema ve (clientes, calendario, dashboard, cobros…)
const RegionesRtc = ({ v, texto, on, conUsuario }) => {
  const [regs, setRegs] = useState([]);
  useEffect(() => {
    api.get("/regions").then(({ data }) => {
      const lista = data || [];
      setRegs(lista);
      // Expedientes anteriores: la región era un texto libre («R1 Y R5»); se toman los códigos que aparezcan
      if (!(v || []).length && texto) {
        const t = String(texto).toUpperCase();
        const enTexto = lista.map((r) => r.codigo).filter((c) => new RegExp(`(^|[^A-Z0-9])${c.toUpperCase()}([^A-Z0-9]|$)`).test(t));
        if (enTexto.length) on(enTexto);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const visibles = regs.filter((r) => r.activo !== false || (v || []).includes(r.codigo));
  const toggle = (c) => on((v || []).includes(c) ? v.filter((x) => x !== c) : [...(v || []), c].sort());
  return (
    <div className="mt-3 border border-agro-green/30 bg-agro-green/5 rounded-md p-3" data-testid="hr-rtc-regiones">
      <div className="text-[10px] uppercase tracking-wider text-agro-green font-semibold">Regiones asignadas al RTC *</div>
      <div className="text-[11px] text-text-secondary mt-0.5 mb-2">
        Marca las regiones que atiende. Rigen su perfil: verá y operará solo con los clientes de estas regiones
        (clientes, proformas, cobros, calendario, dashboard y reportes).
        {!conUsuario && <span className="text-agro-yellow"> Vincúlale su usuario del sistema (abajo) para que se aplique a su acceso.</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        {visibles.map((r) => {
          const on_ = (v || []).includes(r.codigo);
          return (
            <button key={r.id || r.codigo} type="button" onClick={() => toggle(r.codigo)} data-testid={`hr-rtc-region-${r.codigo}`}
              className={`px-3 py-1.5 rounded-md border text-xs font-medium ${on_ ? "bg-agro-green/15 border-agro-green text-agro-green" : "border-border text-text-secondary hover:border-agro-green/40"}`}>
              {on_ && <CheckIcon size={11} weight="bold" className="inline align-[-1px] mr-0.5" aria-hidden="true" />}{r.codigo}{r.nombre && r.nombre !== r.codigo ? ` · ${r.nombre}` : ""}{r.activo === false ? " (inactiva)" : ""}
            </button>
          );
        })}
      </div>
      {!(v || []).length && <div className="text-[11px] text-agro-yellow mt-2">Sin regiones: el RTC no verá clientes.</div>}
    </div>
  );
};

const RegionField = ({ v, on }) => {
  const [regs, setRegs] = useState([]);
  useEffect(() => {
    api.get("/regions", { params: { activo: true } }).then(({ data }) => setRegs(data || [])).catch(() => {});
  }, []);
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Región</label>
      <input list="hr-regiones" value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid="emp-region-input"
        className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
      <datalist id="hr-regiones">
        {regs.map((r) => <option key={r.id} value={r.codigo} />)}
      </datalist>
    </div>
  );
};

const Inp = ({ l, v, on, type = "text", step, req, span = "", disabled = false }) => (
  <div className={span}>
    <label className="text-[9px] uppercase tracking-wider text-text-muted mb-1 block">{l} {req && <span className="text-red-400">*</span>}</label>
    <input type={type} step={step} value={v ?? ""} onChange={(e) => on(e.target.value)} disabled={disabled} className={`w-full bg-app border border-border rounded px-2 py-1.5 text-sm ${disabled ? "opacity-40 cursor-not-allowed" : ""}`} />
  </div>
);
const Sel = ({ l, v, on, opts, testid }) => (
  <div>
    <label className="text-[9px] uppercase tracking-wider text-text-muted mb-1 block">{l}</label>
    <select value={v} onChange={(e) => on(e.target.value)} data-testid={testid} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm">
      {opts.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
    </select>
  </div>
);

/* ===================== 13.2 NÓMINA ===================== */
function NominaTab() {
  const [periods, setPeriods] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPeriodModal, setNewPeriodModal] = useState(false);
  const [extrasModal, setExtrasModal] = useState(null);
  const [payModal, setPayModal] = useState(false);
  const sendAll = async (p) => {
    try { const { data } = await api.post(`/hr/payroll/periods/${p.id}/send-payslips`); toast.success(`Colillas enviadas: ${data.enviados}${data.sin_email.length ? ` · sin correo: ${data.sin_email.length}` : ""}`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const sendOne = async (l) => {
    try { const { data } = await api.post(`/hr/payroll/lines/${l.id}/send-email`, {}); toast.success(`Colilla enviada a ${data.to.join(", ")}`); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/hr/payroll/periods"); setPeriods(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadDetail = async (p) => {
    setSelected(p);
    try { const { data } = await api.get(`/hr/payroll/periods/${p.id}/lines`); setDetail(data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const calculate = async (p) => {
    if (!window.confirm(`Calcular nómina ${p.nombre}? Suma GRATIVA RTC + viáticos liquidados del período.`)) return;
    try {
      const { data } = await api.post(`/hr/payroll/periods/${p.id}/calculate`);
      toast.success(`Calculado: ${data.lineas} líneas · Neto ${fmtL(data.totales.neto)}`);
      load(); loadDetail(p);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const approve = async (p) => {
    if (!window.confirm(`Aprobar la nómina ${p.nombre}? Se generará el asiento contable (sueldos, cargas sociales, retenciones IHSS/RAP/ISR y sueldos por pagar).`)) return;
    try {
      await api.post(`/hr/payroll/periods/${p.id}/approve`);
      toast.success("Nómina aprobada y contabilizada");
      load(); loadDetail(p);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-border/60 flex justify-between items-center">
          <h3 className="font-heading text-sm">Períodos</h3>
          <button onClick={() => setNewPeriodModal(true)} data-testid="new-period" className="text-xs flex items-center gap-1 px-2 py-1 bg-agro-green text-black font-medium rounded">
            <PlusIcon size={12} /> Nuevo
          </button>
        </div>
        <div className="max-h-[700px] overflow-y-auto">
          {loading ? <div className="p-6 text-center text-text-muted">Cargando…</div>
          : periods.length === 0 ? <div className="p-6 text-center text-text-muted text-sm">Sin períodos</div>
          : periods.map((p) => (
            <button key={p.id} onClick={() => loadDetail(p)} data-testid={`period-${p.nombre}`}
              className={`w-full text-left p-3 border-b border-border/30 hover:bg-white/[0.02] transition ${selected?.id === p.id ? "bg-agro-green/5 border-l-2 border-l-agro-green" : ""}`}>
              <div className="text-sm font-medium">{p.nombre}</div>
              <div className="text-[10px] text-text-muted">{fmtDate(p.fecha_inicio)} → {fmtDate(p.fecha_fin)} · {p.tipo}</div>
              <PeriodBadge e={p.estado} />
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2 bg-card border border-border/60 rounded-lg overflow-hidden">
        {!detail ? (
          <div className="p-10 text-center text-text-muted">Selecciona un período para ver el detalle</div>
        ) : (
          <>
            <div className="p-4 border-b border-border/60 flex justify-between items-center flex-wrap gap-2">
              <div>
                <h3 className="font-heading text-base">{detail.period.nombre}</h3>
                <div className="text-[10px] text-text-muted">Estado: <PeriodBadge e={detail.period.estado} /></div>
              </div>
              <div className="flex gap-2">
                {(detail.period.estado === "abierto" || detail.period.estado === "calculado") && (
                  <button onClick={() => setExtrasModal(detail.period)} data-testid="extras-payroll" className="text-xs flex items-center gap-1 px-3 py-1.5 border border-border rounded text-text-secondary hover:text-agro-green">
                    <PencilSimpleIcon size={12} /> Novedades
                  </button>
                )}
                {(detail.period.estado === "aprobado" || detail.period.estado === "pagado") && (
                  <button onClick={() => sendAll(detail.period)} data-testid="send-payslips" className="text-xs flex items-center gap-1 px-3 py-1.5 border border-border rounded text-text-secondary hover:text-agro-green">
                    <EnvelopeSimpleIcon size={12} /> Enviar colillas
                  </button>
                )}
                {detail.period.estado === "aprobado" && (
                  <button onClick={() => setPayModal(true)} data-testid="pay-payroll" className="text-xs flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 text-blue-400 border border-blue-500/40 rounded">
                    <MoneyIcon size={12} /> Pagar planilla
                  </button>
                )}
                {detail.period.estado === "abierto" || detail.period.estado === "calculado" ? (
                  <button onClick={() => calculate(detail.period)} data-testid="calc-payroll" className="text-xs flex items-center gap-1 px-3 py-1.5 bg-agro-yellow/20 text-agro-yellow border border-agro-yellow/40 rounded hover:bg-agro-yellow/30">
                    <CalculatorIcon size={12} /> Calcular
                  </button>
                ) : null}
                {detail.period.estado === "calculado" && (
                  <button onClick={() => approve(detail.period)} data-testid="approve-payroll" className="text-xs flex items-center gap-1 px-3 py-1.5 bg-agro-green text-black font-medium rounded">
                    <CheckCircleIcon size={12} weight="bold" /> Aprobar y contabilizar
                  </button>
                )}
              </div>
            </div>

            {/* Totales */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3 bg-sidebar/30 border-b border-border/40">
              <Tot l="Devengado" v={fmtL(detail.totales.devengado)} c="green" />
              <Tot l="Deducciones" v={fmtL(detail.totales.deducido)} c="yellow" />
              <Tot l="ISR" v={fmtL(detail.totales.isr)} c="muted" />
              <Tot l="Cargas patr." v={fmtL(detail.totales.cargas_patronales)} c="muted" />
              <Tot l="NETO" v={fmtL(detail.totales.neto)} c="green" big />
            </div>

            {/* Líneas */}
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full text-xs">
                <thead className="bg-sidebar/60 sticky top-0 z-10">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                    <th className="p-2 pl-3">Empleado</th>
                    <th className="p-2 text-right">Salario</th>
                    <th className="p-2 text-right">H.Ex.</th>
                    <th className="p-2 text-right" title="Gratificación Variable">GRATIVA</th>
                    <th className="p-2 text-right">Bonif.</th>
                    <th className="p-2 text-right">Viát.</th>
                    <th className="p-2 text-right text-agro-green">Devengado</th>
                    <th className="p-2 text-right">IHSS</th>
                    <th className="p-2 text-right">RAP</th>
                    <th className="p-2 text-right">ISR</th>
                    <th className="p-2 text-right">Otras</th>
                    <th className="p-2 text-right text-red-400">Deduc.</th>
                    <th className="p-2 text-right">Prést.</th>
                    <th className="p-2 pr-3 text-right text-agro-green font-bold">Neto</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {detail.lineas.length === 0 ? <tr><td colSpan="15" className="p-6 text-center text-text-muted">Aún sin calcular. Click &quot;Calcular&quot;</td></tr>
                  : detail.lineas.map((l) => (
                    <tr key={l.id} className="border-t border-border/30 hover:bg-white/[0.02]">
                      <td className="p-2 pl-3"><div className="font-medium">{l.empleado_nombre}</div><div className="text-[10px] text-text-muted">{l.empleado_codigo} · {l.departamento}</div></td>
                      <td className="p-2 text-right">{fmtL(l.salario_periodo)}</td>
                      <td className="p-2 text-right">{l.horas_extra > 0 ? fmtL(l.horas_extra) : "—"}</td>
                      <td className="p-2 text-right text-agro-yellow">{l.comisiones > 0 ? fmtL(l.comisiones) : "—"}</td>
                      <td className="p-2 text-right">{l.bonificaciones > 0 ? fmtL(l.bonificaciones) : "—"}</td>
                      <td className="p-2 text-right">{l.viaticos_liquidados > 0 ? fmtL(l.viaticos_liquidados) : "—"}</td>
                      <td className="p-2 text-right text-agro-green font-medium">{fmtL(l.total_devengado)}</td>
                      <td className="p-2 text-right">{fmtL(l.ihss_empleado)}</td>
                      <td className="p-2 text-right">{fmtL(l.rap_empleado)}</td>
                      <td className="p-2 text-right">{fmtL(l.isr)}</td>
                      <td className="p-2 text-right">{l.otras_deducciones > 0 ? fmtL(l.otras_deducciones) : "—"}</td>
                      <td className="p-2 text-right text-red-400">{fmtL(l.total_deducciones)}</td>
                      <td className="p-2 text-right">{l.prestamos_deduccion > 0 ? fmtL(l.prestamos_deduccion) : "—"}</td>
                      <td className="p-2 pr-3 text-right text-agro-green font-bold">{fmtL(l.neto_a_pagar)}</td>
                      <td className="p-2 whitespace-nowrap">
                        <button onClick={() => openPdf(`/hr/payroll/lines/${l.id}/pdf`)} className="p-1 text-text-muted hover:text-agro-yellow" title="Colilla PDF"><FilePdfIcon size={14} /></button>
                        {(detail.period.estado === "aprobado" || detail.period.estado === "pagado") && <button onClick={() => sendOne(l)} className="p-1 text-text-muted hover:text-agro-green" title="Enviar colilla por correo" aria-label="Enviar colilla por correo"><EnvelopeSimpleIcon size={14} /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {detail.lineas.length > 0 && <DeptReport periodId={detail.period.id} />}
          </>
        )}
      </div>

      {newPeriodModal && <NewPeriodModal onClose={() => setNewPeriodModal(false)} onCreated={() => { setNewPeriodModal(false); load(); }} />}
      {extrasModal && <ExtrasModal period={extrasModal} onClose={() => setExtrasModal(null)} onSaved={() => { setExtrasModal(null); load(); }} />}
      {payModal && detail && <PayModal period={detail.period} total={detail.totales.neto} onClose={() => setPayModal(false)} onPaid={() => { setPayModal(false); load(); loadDetail(detail.period); }} />}
      <div className="lg:col-span-3"><LoansPanel /></div>
    </div>
  );
}

function PeriodBadge({ e }) {
  const m = {
    abierto: "bg-app text-text-secondary border-border",
    calculado: "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30",
    aprobado: "bg-agro-green/10 text-agro-green border-agro-green/30",
    pagado: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    anulado: "bg-red-500/10 text-red-400 border-red-500/30",
  }[e] || "border-border";
  return <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${m}`}>{e}</span>;
}

function Tot({ l, v, c, big }) {
  const cls = { green: "text-agro-green", yellow: "text-agro-yellow", muted: "text-text-secondary" }[c];
  return <div className="bg-card/50 border border-border/40 rounded p-2">
    <div className="text-[9px] uppercase tracking-wider text-text-muted">{l}</div>
    <div className={`font-heading ${big ? "text-xl" : "text-base"} ${cls}`}>{v}</div>
  </div>;
}

function NewPeriodModal({ onClose, onCreated }) {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [f, setF] = useState({ nombre: `${today.toLocaleString("es", { month: "long", year: "numeric" })}`, tipo: "mensual", fecha_inicio: startOfMonth, fecha_fin: endOfMonth });

  const save = async () => {
    try { await api.post("/hr/payroll/periods", f); toast.success("Período creado"); onCreated(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const m = useModalForm({ value: f, onClose, onSave: save });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-md">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h3 className="font-heading text-lg">Nuevo período de planilla</h3>
          <BotonCerrar onClick={m.cerrar} />
        </div>
        <div className="p-4 space-y-3">
          <Inp l="Nombre" v={f.nombre} on={(v) => setF({...f, nombre: v})} />
          <Sel l="Tipo" v={f.tipo} on={(v) => setF({...f, tipo: v})} opts={[["mensual", "Mensual"], ["quincenal", "Quincenal"]]} />
          <div className="grid grid-cols-2 gap-2">
            <Inp l="Desde" type="date" v={f.fecha_inicio} on={(v) => setF({...f, fecha_inicio: v})} />
            <Inp l="Hasta" type="date" v={f.fecha_fin} on={(v) => setF({...f, fecha_fin: v})} />
          </div>
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button onClick={m.cerrar} className="px-3 py-1.5 text-sm border border-border rounded">Cancelar</button>
          <button onClick={m.guardar} disabled={m.saving || !(f.nombre && f.fecha_inicio && f.fecha_fin)} className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded disabled:opacity-40">{m.saving ? "Guardando…" : "Crear"}</button>
        </div>
      </div>
    </div>
  );
}

/* ===================== 13.3 AUSENCIAS ===================== */
function AusenciasTab() {
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState("pendiente");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, e] = await Promise.all([api.get("/hr/absences", { params: estado ? { estado } : {} }), api.get("/hr/employees")]);
      setItems(a.data); setEmps(e.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [estado]);

  const approve = async (a) => {
    try { await api.post(`/hr/absences/${a.id}/approve`); toast.success("Aprobada"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const [resched, setResched] = useState(null);
  const rm = useModalForm({ value: resched || undefined, abierto: Boolean(resched), onClose: () => setResched(null), onSave: () => doResched() });
  const doResched = async () => {
    try { await api.post(`/hr/absences/${resched.id}/reschedule`, { nueva_fecha_inicio: resched.fi, nueva_fecha_fin: resched.ff, motivo: resched.motivo || "" }); toast.success("Reprogramada"); setResched(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const attach = async (a, file) => {
    if (!file) return;
    try { await uploadRespaldo(`/hr/absences/${a.id}/respaldo`, file); toast.success("Respaldo adjuntado"); load(); }
    catch (e) { toast.error(apiErr(e) || e.message); }
  };
  const deny = async (a) => {
    const motivo = window.prompt(`Motivo del rechazo de la solicitud de ${a.empleado_nombre}:`);
    if (!motivo) return;
    try { await api.post(`/hr/absences/${a.id}/deny`, { motivo }); toast.success("Denegada"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-3">
        <div className="flex gap-1">
          {["pendiente", "aprobada", "reprogramada", "denegada", ""].map((e) => (
            <button key={e || "todas"} onClick={() => setEstado(e)} className={`text-xs px-3 py-1.5 rounded border ${estado === e ? "bg-agro-green/10 text-agro-green border-agro-green/30" : "border-border text-text-muted"}`}>
              {e || "Todas"}
            </button>
          ))}
        </div>
        <button onClick={() => setCreating(true)} data-testid="new-absence" className="bg-agro-green text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 hover:bg-agro-green-hover">
          <PlusIcon size={14} weight="bold" /> Nueva solicitud
        </button>
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">Tipo</th><th className="p-3">Empleado</th><th className="p-3">Período</th>
              <th className="p-3 text-right">Días</th><th className="p-3">Motivo</th>
              <th className="p-3">Estado</th><th className="p-3 pr-5 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="7" className="p-6 text-center text-text-muted">Cargando…</td></tr>
            : items.length === 0 ? <tr><td colSpan="7" className="p-10 text-center text-text-muted">Sin solicitudes</td></tr>
            : items.map((a) => (
              <tr key={a.id} className="border-t border-border/30">
                <td className="p-3 pl-5"><TipoAusencia t={a.tipo} /></td>
                <td className="p-3">{a.empleado_nombre}</td>
                <td className="p-3 text-text-secondary">{fmtDate(a.fecha_inicio)} → {fmtDate(a.fecha_fin)}</td>
                <td className="p-3 text-right font-medium">{a.dias}</td>
                <td className="p-3 text-text-secondary text-[11px] max-w-xs truncate" title={a.motivo}>{a.motivo}</td>
                <td className="p-3"><EstadoAusencia e={a.estado} motivo={a.denegado_motivo} /></td>
                <td className="p-3 pr-5 text-right">
                  <div className="inline-flex gap-1 items-center">
                    {(a.respaldos || []).map((r, i) => <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" className="p-1 text-agro-yellow" title={r.descripcion || "Respaldo"}><PaperclipIcon size={13} /></a>)}
                    <label className="p-1 text-text-muted hover:text-agro-green cursor-pointer" title="Subir respaldo"><PlusIcon size={12} /><input type="file" hidden accept="image/*,.pdf" onChange={(e) => attach(a, e.target.files?.[0])} /></label>
                    {["pendiente", "aprobada", "reprogramada"].includes(a.estado) && <button onClick={() => setResched({ id: a.id, fi: a.fecha_inicio.slice(0, 10), ff: a.fecha_fin.slice(0, 10), motivo: "", nombre: a.empleado_nombre })} className="p-1.5 text-blue-400 hover:bg-blue-500/10 rounded" title="Reprogramar" data-testid={`resched-${a.id}`}><ArrowsClockwiseIcon size={14} /></button>}
                    {a.estado === "pendiente" && <>
                      <button onClick={() => approve(a)} className="p-1.5 text-agro-green hover:bg-agro-green/10 rounded" title="Aprobar" aria-label="Aprobar"><CheckCircleIcon size={14} weight="bold" /></button>
                      <button onClick={() => deny(a)} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded" title="Denegar" aria-label="Denegar"><XCircleIcon size={14} weight="bold" /></button>
                    </>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <NewAbsenceModal emps={emps} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {resched && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...rm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md" data-testid="resched-modal">
            <div className="p-4 border-b border-border flex items-center justify-between"><h3 className="font-heading text-lg">Reprogramar — {resched.nombre}</h3><BotonCerrar onClick={rm.cerrar} /></div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <Inp l="Nueva fecha inicio" type="date" v={resched.fi} on={(v) => setResched({ ...resched, fi: v })} />
                <Inp l="Nueva fecha fin" type="date" v={resched.ff} on={(v) => setResched({ ...resched, ff: v })} />
              </div>
              <Inp l="Motivo de la reprogramación" v={resched.motivo} on={(v) => setResched({ ...resched, motivo: v })} />
              <p className="text-[11px] text-text-muted">Queda registrado en auditoría y en el historial de la solicitud.</p>
            </div>
            <div className="p-4 border-t border-border flex justify-end gap-2">
              <button onClick={rm.cerrar} className="px-3 py-1.5 text-sm border border-border rounded">Cancelar</button>
              <button onClick={rm.guardar} disabled={rm.saving || !(resched.fi && resched.ff)} className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded disabled:opacity-40" data-testid="resched-save">{rm.saving ? "Guardando…" : "Reprogramar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TipoAusencia({ t }) {
  const m = {
    vacaciones: { c: "bg-blue-500/10 text-blue-400 border-blue-500/30", l: "Vacaciones" },
    permiso: { c: "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30", l: "Permiso" },
    incapacidad: { c: "bg-orange-500/10 text-orange-400 border-orange-500/30", l: "Incapacidad" },
  }[t] || { c: "border-border", l: t };
  return <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${m.c}`}>{m.l}</span>;
}

function EstadoAusencia({ e, motivo }) {
  const m = {
    pendiente: "bg-app text-text-secondary border-border",
    aprobada: "bg-agro-green/10 text-agro-green border-agro-green/30",
    denegada: "bg-red-500/10 text-red-400 border-red-500/30",
    reprogramada: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  }[e] || "border-border";
  return <div>
    <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${m}`}>{e}</span>
    {e === "denegada" && motivo && <div className="text-[10px] text-red-300 mt-0.5 max-w-[120px] truncate" title={motivo}>{motivo}</div>}
  </div>;
}

function NewAbsenceModal({ emps, onClose, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ empleado_id: "", tipo: "permiso", subtipo: "", motivo: "", fecha_inicio: today, fecha_fin: today });

  const save = async () => {
    if (!f.empleado_id || !f.motivo) return toast.error("Empleado y motivo son obligatorios");
    try { await api.post("/hr/absences", f); toast.success("Solicitud creada"); onCreated(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const m = useModalForm({ value: f, onClose, onSave: save });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-md">
        <div className="p-4 border-b border-border flex items-center justify-between"><h3 className="font-heading text-lg">Nueva solicitud</h3><BotonCerrar onClick={m.cerrar} /></div>
        <div className="p-4 space-y-3">
          <Sel l="Tipo" v={f.tipo} on={(v) => setF({...f, tipo: v, subtipo: ""})} opts={[
            ["permiso", "Permiso"], ["incapacidad", "Incapacidad"], ["vacaciones", "Vacaciones"],
          ]} />
          {f.tipo === "permiso" && <Sel l="Goce" v={f.subtipo} on={(v) => setF({...f, subtipo: v})} opts={[["", "—"], ["con_goce", "Con goce"], ["sin_goce", "Sin goce"]]} />}
          {f.tipo === "incapacidad" && <Sel l="Tipo" v={f.subtipo} on={(v) => setF({...f, subtipo: v})} opts={[["", "—"], ["comun", "Común"], ["maternidad", "Maternidad"], ["riesgo_profesional", "Riesgo profesional"]]} />}
          <Sel l="Empleado" v={f.empleado_id} on={(v) => setF({...f, empleado_id: v})} opts={[["", "— Selecciona —"], ...emps.map((e) => [e.id, `${e.nombre} (${e.codigo})`])]} />
          <div className="grid grid-cols-2 gap-2">
            <Inp l="Desde" type="date" v={f.fecha_inicio} on={(v) => setF({...f, fecha_inicio: v})} />
            <Inp l="Hasta" type="date" v={f.fecha_fin} on={(v) => setF({...f, fecha_fin: v})} />
          </div>
          <Inp l="Motivo" v={f.motivo} on={(v) => setF({...f, motivo: v})} />
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button onClick={m.cerrar} className="px-3 py-1.5 text-sm border border-border rounded">Cancelar</button>
          <button onClick={m.guardar} disabled={m.saving || !(f.empleado_id && f.motivo)} title="Empleado y motivo son obligatorios" className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded disabled:opacity-40">{m.saving ? "Guardando…" : "Crear"}</button>
        </div>
      </div>
    </div>
  );
}

/* ===================== 13.4 ACTAS ===================== */
function ActasTab() {
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, e] = await Promise.all([api.get("/hr/acts"), api.get("/hr/employees")]);
      setItems(a.data); setEmps(e.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const attachActa = async (a, file) => {
    if (!file) return;
    try { await uploadRespaldo(`/hr/acts/${a.id}/respaldo`, file); toast.success("Respaldo adjuntado"); load(); }
    catch (e) { toast.error(apiErr(e) || e.message); }
  };
  const emailActa = async (a) => {
    try { const { data } = await api.post(`/hr/acts/${a.id}/send-email`, {}); toast.success(`Constancia enviada a ${data.to.join(", ")}`); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-xs text-text-secondary">{items.length} actas registradas en expedientes</div>
        <button onClick={() => setCreating(true)} className="bg-agro-green text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2" data-testid="new-act">
          <PlusIcon size={14} weight="bold" /> Nueva acta
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading ? <div className="col-span-3 text-center text-text-muted p-10">Cargando…</div>
        : items.length === 0 ? <div className="col-span-3 text-center text-text-muted p-10">Sin actas</div>
        : items.map((a) => (
          <div key={a.id} className={`bg-card border rounded-lg p-4 ${a.tipo === "reconocimiento" ? "border-agro-green/30" : a.tipo === "suspension" ? "border-red-500/40" : "border-agro-yellow/30"}`}>
            <div className="flex justify-between items-start">
              <TipoActa t={a.tipo} />
              <div className="flex gap-1">
                <label className="text-text-muted hover:text-agro-green cursor-pointer" title="Subir respaldo"><PaperclipIcon size={14} /><input type="file" hidden accept="image/*,.pdf" onChange={(e) => attachActa(a, e.target.files?.[0])} /></label>
                <button onClick={() => openPdf(`/hr/acts/${a.id}/pdf`)} className="text-text-muted hover:text-agro-green" title="Constancia PDF" data-testid={`acta-pdf-${a.id}`}><PrinterIcon size={14} /></button>
                <button onClick={() => emailActa(a)} className="text-text-muted hover:text-agro-green" title="Enviar por correo" aria-label="Enviar por correo"><EnvelopeSimpleIcon size={14} /></button>
              </div>
            </div>
            <div className="font-heading text-sm mt-2">{a.empleado_nombre}</div>
            <div className="text-[10px] text-text-muted">{a.empleado_codigo}</div>
            <p className="text-xs text-text-secondary mt-2">{a.motivo}</p>
            {a.detalle && <p className="text-[10px] text-text-muted mt-1 line-clamp-2">{a.detalle}</p>}
            {(a.respaldos || []).length > 0 && <div className="text-[10px] mt-1">{a.respaldos.map((r, i) => <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" className="text-agro-yellow mr-2"><PaperclipIcon size={11} className="inline align-[-2px]" aria-hidden="true" /> {r.descripcion || `Respaldo ${i + 1}`}</a>)}</div>}
            <div className="border-t border-border/30 mt-3 pt-2 text-[10px] text-text-muted flex justify-between">
              <span>{fmtDate(a.fecha)}</span>
              <span>Emitida: {a.emitido_por_nombre || a.emitido_por}</span>
            </div>
          </div>
        ))}
      </div>

      {creating && <NewActModal emps={emps} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function TipoActa({ t }) {
  const m = {
    reconocimiento: { c: "bg-agro-green/10 text-agro-green border-agro-green/30", l: "Reconocimiento", icon: MedalIcon },
    amonestacion_verbal: { c: "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30", l: "Amon. verbal", icon: WarningIcon },
    amonestacion_escrita: { c: "bg-orange-500/10 text-orange-400 border-orange-500/30", l: "Amon. escrita", icon: WarningIcon },
    suspension: { c: "bg-red-500/10 text-red-400 border-red-500/30", l: "Suspensión", icon: WarningIcon },
  }[t];
  if (!m) return null;
  const Icon = m.icon;
  return <span className={`text-[9px] uppercase px-2 py-1 rounded border flex items-center gap-1 inline-flex ${m.c}`}>
    <Icon size={10} weight="fill" /> {m.l}
  </span>;
}

function NewActModal({ emps, onClose, onCreated }) {
  const [f, setF] = useState({ empleado_id: "", tipo: "reconocimiento", motivo: "", detalle: "", fecha: new Date().toISOString().slice(0, 10) });
  const save = async () => {
    if (!f.empleado_id || !f.motivo) return toast.error("Empleado y motivo son obligatorios");
    try { await api.post("/hr/acts", f); toast.success("Acta registrada en el expediente"); onCreated(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const m = useModalForm({ value: f, onClose, onSave: save });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-md">
        <div className="p-4 border-b border-border flex items-center justify-between"><h3 className="font-heading text-lg">Nueva acta oficial</h3><BotonCerrar onClick={m.cerrar} /></div>
        <div className="p-4 space-y-3">
          <Sel l="Tipo de acta" v={f.tipo} on={(v) => setF({...f, tipo: v})} opts={[
            ["reconocimiento", "Reconocimiento (mérito/felicitación)"],
            ["amonestacion_verbal", "Amonestación verbal (leve)"],
            ["amonestacion_escrita", "Amonestación escrita (formal)"],
            ["suspension", "Suspensión (medida disciplinaria mayor)"],
          ]} />
          <Sel l="Empleado" v={f.empleado_id} on={(v) => setF({...f, empleado_id: v})} opts={[["", "— Selecciona —"], ...emps.map((e) => [e.id, `${e.nombre} (${e.codigo})`])]} />
          <Inp l="Fecha" type="date" v={f.fecha} on={(v) => setF({...f, fecha: v})} />
          <Inp l="Motivo / Resumen" v={f.motivo} on={(v) => setF({...f, motivo: v})} />
          <div>
            <label className="text-[9px] uppercase tracking-wider text-text-muted mb-1 block">Detalle</label>
            <textarea rows={4} value={f.detalle} onChange={(e) => setF({...f, detalle: e.target.value})} className="w-full bg-app border border-border rounded px-3 py-2 text-sm" />
          </div>
          <div className="text-[10px] text-text-muted bg-app/40 border border-border rounded p-2">
            Esta acta queda registrada en el expediente oficial del empleado y se podrá imprimir como constancia.
          </div>
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button onClick={m.cerrar} className="px-3 py-1.5 text-sm border border-border rounded">Cancelar</button>
          <button onClick={m.guardar} disabled={m.saving || !(f.empleado_id && f.motivo)} title="Empleado y motivo son obligatorios" className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded disabled:opacity-40">{m.saving ? "Guardando…" : "Registrar acta"}</button>
        </div>
      </div>
    </div>
  );
}

/* ===================== 13.5 REPORTES ===================== */
function ReportesTab({ isAdmin }) {
  const [summary, setSummary] = useState(null);
  useEffect(() => { api.get("/hr/summary").then(({ data }) => setSummary(data)); }, []);
  if (!summary) return <div className="text-center text-text-muted p-10">Cargando…</div>;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Empleados activos" v={summary.totales.activos} c="green" />
        <KpiCard label="Suspendidos" v={summary.totales.suspendidos} c="yellow" />
        <KpiCard label="Bajas" v={summary.totales.bajas} c="red" />
        <KpiCard label="Solicitudes pendientes" v={summary.solicitudes_pendientes} c="yellow" />
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-border/60">
          <h3 className="font-heading text-sm">Costo de planilla por departamento (salario base mensual)</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-sidebar/40">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">Departamento</th>
              <th className="p-3 text-right"># Empleados</th>
              <th className="p-3 pr-5 text-right">Salario base total</th>
            </tr>
          </thead>
          <tbody>
            {summary.por_departamento.map((d, i) => (
              <tr key={i} className="border-t border-border/30">
                <td className="p-3 pl-5">{d.departamento}</td>
                <td className="p-3 text-right">{d.n}</td>
                <td className="p-3 pr-5 text-right font-medium">{d.monto == null ? "—" : fmtL(d.monto)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <HrReportesTab />
      <PayrollParamsEditor isAdmin={isAdmin} />

      <div className="bg-card border border-border/60 rounded-lg p-4">
        <h3 className="font-heading text-sm mb-3">Tablas vigentes (resumen)</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          <Info l="Techo IHSS" v={fmtL(summary.tablas_honduras_2026.ihss_techo)} />
          <Info l="IHSS empleado %" v={`${(summary.tablas_honduras_2026.ihss_pct_empleado * 100).toFixed(1)}%`} />
          <Info l="Techo RAP" v={fmtL(summary.tablas_honduras_2026.rap_techo)} />
          <Info l="RAP empleado %" v={`${(summary.tablas_honduras_2026.rap_pct_empleado * 100).toFixed(1)}%`} />
          <Info l="INFOP patronal %" v={`${(summary.tablas_honduras_2026.infop_pct_empleador * 100).toFixed(1)}%`} />
        </div>
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">ISR — Tramos anuales</div>
          <table className="w-full text-xs">
            <thead><tr className="text-text-muted text-[10px]"><th className="text-left p-1">Desde</th><th className="text-left p-1">Hasta</th><th className="text-right p-1">Tasa</th></tr></thead>
            <tbody>
              <tr className="border-t border-border/30"><td className="p-1">L 0</td><td className="p-1">{fmtL(summary.tablas_honduras_2026.isr_tramos_anuales[0][0])}</td><td className="p-1 text-right text-agro-green">EXENTO</td></tr>
              {summary.tablas_honduras_2026.isr_tramos_anuales.slice(1).map((t, i) => (
                <tr key={i} className="border-t border-border/30">
                  <td className="p-1">{fmtL(summary.tablas_honduras_2026.isr_tramos_anuales[i][0])}</td>
                  <td className="p-1">{fmtL(t[0])}</td>
                  <td className="p-1 text-right">{(summary.tablas_honduras_2026.isr_tramos_anuales[i][1] * 100).toFixed(0)}%</td>
                </tr>
              ))}
              <tr className="border-t border-border/30"><td className="p-1">{fmtL(summary.tablas_honduras_2026.isr_tramos_anuales.slice(-1)[0][0])}</td><td className="p-1">∞</td><td className="p-1 text-right">25%</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, v, c }) {
  const cl = { green: "text-agro-green", yellow: "text-agro-yellow", red: "text-red-400" }[c];
  return <div className="bg-card border border-border/60 rounded-lg p-4">
    <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
    <div className={`font-heading text-2xl ${cl}`}>{v}</div>
  </div>;
}
